import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, Exam, CalendarBlank, Clock, Warning, ShieldCheck, CaretRight, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = 'https://customer-assets.emergentagent.com/job_gm-class-training/artifacts/1szu61lb_Logo%20G%26M.png';

export default function PublicTestPage() {
  const { trainingId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rut, setRut] = useState('');
  const [stage, setStage] = useState('rut');
  const [verifying, setVerifying] = useState(false);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/trainings/${trainingId}/test-info`)
      .then(r => setInfo(r.data))
      .catch(() => setInfo({ error: true }))
      .finally(() => setLoading(false));
  }, [trainingId]);

  const fDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' }) : '-';
  const fTime = (iso) => iso ? new Date(iso).toLocaleTimeString('es-CL', { timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit' }) : '-';

  const handleVerify = async () => {
    if (!rut.trim()) { toast.error('Ingresa tu RUT'); return; }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/public/trainings/${trainingId}/verify-rut`, { rut: rut.trim() });
      if (data.already_evaluated) {
        setResult({ already: true, worker_name: data.worker_name });
        setStage('result');
      } else if (!data.test_available) {
        setResult({ attendedOnly: true, worker_name: data.worker_name, message: data.message });
        setStage('result');
      } else {
        setSession(data);
        setStage('test');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pudimos validar tu RUT');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    const payload = Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer }));
    if (payload.length < session.questions.length) { toast.error('Responde todas las preguntas'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/trainings/${trainingId}/submit-test`, {
        attempt_id: session.attempt_id, answers: payload
      });
      setResult(data);
      setStage('result');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar la prueba');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  }

  const SessionRow = () => (
    <div className="flex items-center gap-4 text-sm text-[#2B4B8C]/60">
      <span className="flex items-center gap-1.5"><CalendarBlank size={16} /> {fDate(info.scheduled_date)}</span>
      <span className="flex items-center gap-1.5"><Clock size={16} /> {fTime(info.scheduled_date)} hrs</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f6fb] py-10 px-4" data-testid="public-test-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <img src={LOGO} alt="G&M Class" className="h-14 mx-auto" />
        </div>

        {info?.error ? (
          <Card className="bg-white border-[#2B4B8C]/10"><CardContent className="p-6 text-center text-red-500">Capacitación no encontrada.</CardContent></Card>
        ) : (
          <>
            {stage === 'rut' && !info.available && (
              <Card className="bg-white border-2 border-amber-200 shadow-sm" data-testid="not-available">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                    <Clock size={30} className="text-amber-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#2B4B8C]">Prueba no disponible</h1>
                  <p className="text-[#2B4B8C]/60">{info.reason}</p>
                  {info.test_mode === 'auto' && (
                    <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800">
                      <Warning size={16} /> Ventana automática: <strong>13:00</strong> a <strong>15:00</strong> hrs (Chile)
                    </div>
                  )}
                  <div className="border-t border-[#2B4B8C]/5 pt-4 mt-2 text-left">
                    <p className="text-xs font-semibold text-[#2B4B8C]/40 tracking-wide mb-1">SESIÓN</p>
                    <p className="text-[#2B4B8C] font-medium">{info.name}</p>
                    <div className="mt-1"><SessionRow /></div>
                  </div>
                </CardContent>
              </Card>
            )}

            {stage === 'rut' && info.available && (
              <div className="space-y-4" data-testid="rut-stage">
                <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-lg bg-[#2BBAA4]/10 flex items-center justify-center shrink-0">
                        <FileText size={22} className="text-[#2BBAA4]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#2BBAA4] tracking-wide">PRUEBA DE CAPACITACIÓN</p>
                        <h1 className="text-lg font-bold text-[#2B4B8C]">{info.name}</h1>
                        <div className="mt-1"><SessionRow /></div>
                        <div className="inline-flex items-center gap-1.5 mt-2 bg-green-50 border border-green-200 rounded-md px-2 py-1 text-xs text-green-700">
                          <ShieldCheck size={14} /> Prueba abierta por el Capacitador
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
                  <CardContent className="p-6 space-y-4">
                    <div>
                      <h2 className="text-lg font-bold text-[#2B4B8C]">Ingresa tu RUT para comenzar</h2>
                      <p className="text-sm text-[#2B4B8C]/60 mt-1">Validaremos (1) que estés inscrito/a a esta sesión y (2) que el Capacitador haya marcado tu asistencia como Presente.</p>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-[#2B4B8C]">RUT (con puntos y guión)</label>
                      <Input
                        value={rut}
                        onChange={(e) => setRut(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                        placeholder="12.345.678-9"
                        className="border-[#2B4B8C]/15 text-lg h-12"
                        data-testid="public-rut-input"
                      />
                    </div>
                    <Button onClick={handleVerify} disabled={verifying} className="w-full h-12 bg-[#2BBAA4] text-black hover:bg-[#239986] text-base" data-testid="public-verify-btn">
                      {verifying ? 'Validando...' : <>Comenzar Prueba <CaretRight size={18} className="ml-1" /></>}
                    </Button>
                    <div className="flex items-center justify-center gap-1.5 text-xs text-[#2B4B8C]/50">
                      <ShieldCheck size={14} /> Tu identidad y resultados quedan registrados de forma segura. La prueba tiene 1 intento por sesión.
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {stage === 'test' && session && (
              <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
                <CardContent className="p-6 space-y-5" data-testid="public-test-questions">
                  <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle size={16} /> Hola <strong>{session.worker_name}</strong>, responde la prueba:
                  </div>
                  {session.questions.map((q, idx) => (
                    <div key={q.id} className="space-y-2 border-b border-[#2B4B8C]/5 pb-4">
                      <p className="text-sm font-medium text-[#2B4B8C]">{idx + 1}. {q.question_text}</p>
                      <div className="space-y-1.5">
                        {(q.question_type === 'true_false' ? [{value:'true',text:'Verdadero'},{value:'false',text:'Falso'}] : q.options).map((opt, i) => {
                          const val = q.question_type === 'true_false' ? opt.value : (opt.value ?? opt.text ?? opt);
                          const label = q.question_type === 'true_false' ? opt.text : (opt.text ?? opt.value ?? opt);
                          const selected = answers[q.id] === val;
                          return (
                            <button key={i} type="button" onClick={() => setAnswers({ ...answers, [q.id]: val })}
                              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${selected ? 'border-[#2BBAA4] bg-[#2BBAA4]/10 text-[#2B4B8C]' : 'border-[#2B4B8C]/10 text-[#2B4B8C]/80 hover:bg-[#f8fafc]'}`}
                              data-testid={`answer-${q.id}-${i}`}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#2B4B8C] text-white hover:bg-[#233d75]" data-testid="public-submit-btn">
                    <Exam size={16} className="mr-1.5" /> {submitting ? 'Enviando...' : 'Enviar prueba'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {stage === 'result' && result && (
              <Card className="bg-white border-[#2B4B8C]/10 shadow-sm">
                <CardContent className="p-8 text-center space-y-3" data-testid="public-result">
                  {result.already ? (
                    <p className="text-[#2B4B8C]">Hola {result.worker_name}, ya fuiste evaluado en esta capacitación.</p>
                  ) : result.attendedOnly ? (
                    <>
                      <CheckCircle size={48} className="text-green-500 mx-auto" />
                      <p className="text-[#2B4B8C] font-medium">Asistencia registrada</p>
                      <p className="text-sm text-[#2B4B8C]/60">{result.message}</p>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={56} className="text-green-500 mx-auto" />
                      <p className="text-xl font-bold text-[#2B4B8C]">Evaluación recepcionada</p>
                      <p className="text-[#2B4B8C]/80" data-testid="public-received-message">
                        Su evaluación ha sido recepcionada. Los resultados estarán disponibles para su empresa a partir de las 14:00 hrs.
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
        <p className="text-center text-xs text-[#9aa7bf] mt-4">© {new Date().getFullYear()} G&amp;M Class</p>
      </div>
    </div>
  );
}
